import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Camera, Copy, Keyboard, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

export const emptyFornitore = {
  nome: "", partita_iva: "", codice_fiscale: "", telefono: "", email: "", pec: "", codice_sdi: "",
  indirizzo: "", citta: "", cap: "", referente: "", iban: "", note: "",
};

const FIELDS = [
  ["nome", "Ragione sociale", 2], ["partita_iva", "Partita IVA"], ["codice_fiscale", "Codice fiscale"],
  ["telefono", "Telefono"], ["email", "Email"], ["pec", "PEC"], ["codice_sdi", "Codice SDI"],
  ["indirizzo", "Indirizzo", 2], ["citta", "Città"], ["cap", "CAP"], ["referente", "Referente"], ["iban", "IBAN"],
];

export const FornitoreEditor = ({ open, onOpenChange, initial, editingId, onSaved }) => {
  const [form, setForm] = useState(emptyFornitore);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm({ ...emptyFornitore, ...(initial || {}) }); }, [open, initial]);

  const save = async () => {
    if (!form.nome.trim()) { toast.error("Inserisci la ragione sociale"); return; }
    setBusy(true);
    const body = Object.fromEntries(Object.keys(emptyFornitore).map((k) => [k, form[k] || ""]));
    try {
      const { data } = editingId ? await api.put(`/fornitori/${editingId}`, body) : await api.post("/fornitori", body);
      toast.success(editingId ? "Anagrafica aggiornata" : "Fornitore salvato");
      onOpenChange(false);
      onSaved?.(data);
    } catch { toast.error("Errore salvataggio"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="fornitore-editor">
        <DialogHeader><DialogTitle>{editingId ? "Modifica anagrafica" : "Nuovo fornitore"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {FIELDS.map(([k, label, span]) => (
            <div key={k} className={span === 2 ? "sm:col-span-2" : ""}>
              <Label>{label}</Label>
              <Input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`fn-${k}`} />
            </div>
          ))}
          <div className="sm:col-span-2"><Label>Note</Label><Textarea rows={2} value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="fn-note" /></div>
        </div>
        <Button data-testid="fn-save" onClick={save} disabled={busy} className="h-11 mt-2">
          {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Salva
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export const CopiaIban = ({ iban, testid }) => {
  const copy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(iban);
    } catch {
      const t = document.createElement("textarea");
      t.value = iban; document.body.appendChild(t); t.select(); document.execCommand("copy"); t.remove();
    }
    toast.success("IBAN copiato");
  };
  return (
    <Button type="button" size="sm" variant="outline" className="h-8 text-xs gap-1 font-mono" onClick={copy} data-testid={testid} title={iban}>
      <Copy className="h-3 w-3" /> Copia IBAN
    </Button>
  );
};

export const NuovoFornitoreChoice = ({ open, onOpenChange, onManual, onExtracted }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const onFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/fornitori/ocr", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      toast.success("Dati estratti: controlla e salva");
      onExtracted(data);
    } catch { toast.error("Estrazione AI non riuscita"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-w-md" data-testid="nuovo-fornitore-choice">
        <DialogHeader><DialogTitle>Nuovo fornitore</DialogTitle></DialogHeader>
        <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden"
               onChange={(e) => onFile(e.target.files?.[0])} data-testid="nuovo-fornitore-file" />
        <div className="grid gap-3">
          <Button variant="outline" className="h-16 justify-start text-base" onClick={onManual} disabled={busy} data-testid="nuovo-fornitore-manuale">
            <Keyboard className="h-5 w-5 mr-3" /> Inserimento Manuale
          </Button>
          <Button className="h-16 justify-start text-base" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="nuovo-fornitore-ai">
            {busy ? <Loader2 className="h-5 w-5 mr-3 animate-spin" /> : <Sparkles className="h-5 w-5 mr-3" />}
            {busy ? "Estrazione in corso..." : "Estrai con AI da Foto/Documento"}
            {!busy && <Camera className="h-4 w-4 ml-auto" />}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">Foto o PDF di fattura, visura camerale, biglietto da visita o carta intestata.</p>
      </DialogContent>
    </Dialog>
  );
};
